import React, { useEffect, useState, useCallback } from "react";
import type ReactECharts from "echarts-for-react";
import { CriticalPeriodBand } from "../hooks/useCriticalPeriods";
import { CriticalBandPill } from "./CriticalBandPill";

interface LabelPos {
  key: string;
  kind: string;
  label: string;
  icon: string;
  color: string;
  startHour: number;
  endHour: number;
  left: number;
  top: number;
  compact: boolean;
}

interface Props {
  bands: CriticalPeriodBand[];
  chartRef: React.RefObject<ReactECharts>;
  repositionTick: number;
}

const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
const ROW_HEIGHT = 28;
const LABEL_GAP = 6;
// label + " · HH:MM–HH:MM" on one row; separator + time ≈ 60px
const estWidth = (label: string, compact: boolean) =>
  compact ? 30 : 34 + label.length * 6.6 + 60;

export function CriticalBandLabels({ bands, chartRef, repositionTick }: Props) {
  const [positions, setPositions] = useState<LabelPos[]>([]);

  const recompute = useCallback(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst || inst.isDisposed?.() || bands.length === 0) {
      setPositions([]);
      return;
    }

    let gridRect: { x: number; y: number; w: number; h: number } | null = null;
    try {
      const model = (inst as any).getModel?.();
      const coord = model?.getComponent?.("grid")?.coordinateSystem;
      const r = coord?.getRect?.();
      if (r) gridRect = { x: r.x, y: r.y, w: r.width, h: r.height };
    } catch { gridRect = null; }

    const pxOf = (index: number): number | null => {
      let p: any;
      try { p = inst.convertToPixel({ xAxisIndex: 0 }, index); } catch { return null; }
      const v = Array.isArray(p) ? p[0] : (p as number);
      return Number.isFinite(v) ? v : null;
    };

    type Cand = { key: string; band: CriticalPeriodBand; center: number; runPx: number };
    const cands: Cand[] = [];
    bands.forEach((band) => {
      band.runs.forEach((run, ri) => {
        const a = pxOf(run.startIndex);
        const b = pxOf(run.endIndex);
        if (a === null || b === null) return;
        if (gridRect && (b < gridRect.x - 4 || a > gridRect.x + gridRect.w + 4)) return;
        cands.push({
          key: `${band.kind}-${ri}`,
          band,
          center: (a + b) / 2,
          runPx: Math.abs(b - a),
        });
      });
    });

    cands.sort((p, q) => p.center - q.center);

    const baseTop = (gridRect?.y ?? 10) + 6;
    const rowRightEdge: number[] = [];

    const next: LabelPos[] = cands.map((c) => {
      const compact = c.runPx > 0 && c.runPx < 70;
      const w = Math.min(estWidth(c.band.label, compact), 220);
      let left = c.center - w / 2;
      if (gridRect) {
        left = Math.max(gridRect.x + 2, Math.min(left, gridRect.x + gridRect.w - w - 2));
      }
      let row = 0;
      while (rowRightEdge[row] !== undefined && left < rowRightEdge[row] + LABEL_GAP) {
        row++;
      }
      rowRightEdge[row] = left + w;
      return {
        key: c.key,
        kind: c.band.kind,
        label: c.band.label,
        icon: c.band.icon,
        color: c.band.color,
        startHour: c.band.startHour,
        endHour: c.band.endHour,
        left,
        top: baseTop + row * ROW_HEIGHT,
        compact,
      };
    });

    setPositions(next);
  }, [bands, chartRef]);

  // Recompute on tick change. Double-pass: rAF for first paint, 220ms for grid layout.
  useEffect(() => {
    const id1 = requestAnimationFrame(recompute);
    const id2 = window.setTimeout(recompute, 220);
    return () => { cancelAnimationFrame(id1); clearTimeout(id2); };
  }, [recompute, repositionTick]);

  // Reposition on chart resize via ResizeObserver — does NOT set state in a
  // loop because it's driven by DOM events, not React render cycles.
  useEffect(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    const dom = inst?.getDom?.() as HTMLElement | null;
    if (!dom) return;
    const ro = new ResizeObserver(() => requestAnimationFrame(recompute));
    ro.observe(dom);
    return () => ro.disconnect();
  }, [recompute, bands]);

  if (positions.length === 0) return null;

  return (
    <div className="cp-band-labels" aria-hidden>
      {positions.map((p) => (
        <CriticalBandPill
          key={p.key}
          icon={p.icon}
          label={p.label}
          time={`${hh(p.startHour)}–${hh(p.endHour)}`}
          iconKind={p.kind}
          color={p.color}
          compact={p.compact}
          style={{ position: "absolute", left: `${p.left}px`, top: `${p.top}px` }}
          title={`${p.label} · ${hh(p.startHour)}–${hh(p.endHour)}`}
        />
      ))}
    </div>
  );
}
